import React, { useState } from 'react';
import {
  BookOpen,
  TrendingUp,
  Activity,
  ShieldAlert,
  BarChart2,
  Database,
  HelpCircle,
  Calculator,
  ChevronRight,
  Sliders,
  Sparkles,
} from 'lucide-react';

interface UserGuideProps {
  onNavigate?: (tab: 'macro' | 'scanner' | 'sectors' | 'studio') => void;
  onOpenCalculator?: () => void;
}

type GuideSection =
  | 'overview'
  | 'charts'
  | 'workflow'
  | 'risk'
  | 'metrics'
  | 'examples'
  | 'd1'
  | 'faq';

export const UserGuide: React.FC<UserGuideProps> = ({
  onNavigate,
  onOpenCalculator,
}) => {
  const [activeSection, setActiveSection] = useState<GuideSection>('overview');
  const [selectedExample, setSelectedExample] = useState<'2330' | '2454' | 'TAIEX'>('2330');

  const navItems: Array<{ id: GuideSection; label: string; icon: React.ReactNode }> = [
    { id: 'overview', label: '核心哲學與雙層架構', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'charts', label: '核心圖表判讀手冊', icon: <Activity className="w-4 h-4" /> },
    { id: 'workflow', label: '高勝率實戰選股步驟', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'risk', label: '風控與 4G 部位計算', icon: <ShieldAlert className="w-4 h-4" /> },
    { id: 'metrics', label: '回測指標與計算公式', icon: <BarChart2 className="w-4 h-4" /> },
    { id: 'examples', label: '歷史真實回測案例', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'd1', label: 'Cloudflare D1 雲端同步', icon: <Database className="w-4 h-4" /> },
    { id: 'faq', label: '常見問題解答 (FAQ)', icon: <HelpCircle className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-4 max-w-7xl mx-auto pb-12 font-sans text-slate-200">
      {/* Top Banner */}
      <div className="stitch-card rounded-xl p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-[#182232] bg-[#0b0f17]">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-terminal-cyan shadow-[0_0_10px_rgba(56,189,248,0.7)] animate-pulse"></span>
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-terminal-cyan">
              INSTITUTIONAL QUANT MANUAL & BACKTEST RUNBOOK
            </span>
          </div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-100">
            科斯托蘭尼雞蛋理論雙層決策系統 — 實戰操作手冊與回測全書
          </h1>
          <p className="text-xs md:text-sm text-slate-400 max-w-3xl leading-relaxed">
            從大盤定錨、主流板塊穿透到雙層共振選股；深入拆解 CAGR、Sharpe、Sortino 等量化回測指標、嚴格防止未來偏誤的 Walk-Forward 滾動驗證，以及 Cloudflare D1 邊緣雲端同步機制。
          </p>
        </div>

        {/* Quick Action Navigation Buttons */}
        <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto shrink-0">
          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate('studio')}
              className="px-3.5 py-2 rounded-lg bg-primary-container hover:bg-blue-600 text-white text-xs font-mono font-semibold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer active:scale-98"
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>前往 ML 工作室回測</span>
            </button>
          )}
          {onOpenCalculator && (
            <button
              type="button"
              onClick={onOpenCalculator}
              className="px-3.5 py-2 rounded-lg bg-[#141923] hover:bg-[#1f2637] border border-[#1e293b] text-sky-300 text-xs font-mono font-semibold flex items-center gap-1.5 transition-all cursor-pointer active:scale-98"
            >
              <Calculator className="w-3.5 h-3.5 text-terminal-cyan" />
              <span>4G 風控計算機</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Two-Column Layout: Sticky Left Navigation + Right Content Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Left Sticky Navigation Menu */}
        <aside className="lg:col-span-3 stitch-card rounded-xl p-2.5 border border-[#182232] bg-[#0b0f17] sticky top-16 z-10 flex flex-col gap-1">
          <div className="px-3 py-2 text-xs font-mono font-semibold text-slate-400 border-b border-[#182232] flex items-center justify-between">
            <span>目錄章節導覽</span>
            <span className="text-[10px] text-sky-400/80">8 CHAPTERS</span>
          </div>

          <nav className="flex flex-col gap-1 mt-1">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full px-3 py-2 rounded-lg flex items-center justify-between text-xs font-medium transition-all text-left cursor-pointer ${
                    isActive
                      ? 'bg-blue-950/70 text-terminal-cyan font-bold border border-blue-500/40 shadow-sm'
                      : 'text-slate-300 hover:text-white hover:bg-[#141923]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={isActive ? 'text-terminal-cyan' : 'text-slate-400'}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 transition-transform ${
                      isActive ? 'rotate-90 text-terminal-cyan' : 'text-slate-600'
                    }`}
                  />
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right Dynamic Content Body */}
        <section className="lg:col-span-9 space-y-4">
          {/* SECTION 1: OVERVIEW */}
          {activeSection === 'overview' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 1 · FOUNDATIONAL ARCHITECTURE
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  科斯托蘭尼投資哲學與雙層決策架構
                </h2>
              </div>

              <div className="space-y-4 text-xs md:text-sm text-slate-300 leading-relaxed font-sans">
                <p>
                  安德烈・科斯托蘭尼（André Kostolany）曾指出：
                  <strong className="text-terminal-cyan">
                    「行情趨勢取決於兩個要素：資金（Geld）與心理（Psychologie）。」
                  </strong>
                  當固執的投資者（堅定者）在市場恐慌、成交量極度萎縮時默默吸納籌碼，行情便醞釀升市；而當猶豫的投資者（散戶追隨者）在爆發性狂熱中蜂擁進場時，行情往往已走到盡頭。
                </p>

                {/* 2-Layer Decision Framework Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                  <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-blue-900/60 text-terminal-cyan border border-blue-500/40">
                        LAYER 1
                      </span>
                      <h3 className="font-bold text-slate-100 text-sm">第一層：大盤宏觀閘門 (Macro Gate)</h3>
                    </div>
                    <p className="text-xs text-slate-300 leading-normal">
                      綜合台股加權指數 (TAIEX)、櫃買指數 (TPEx)、元大台灣50 (0050)、MSCI台灣指數與台指期近月基差 (Futures Basis)，判斷全市場處於牛市 (BULL_ZONE)、盤整 (SIDEWAYS) 還是空頭 (BEAR_ZONE)，決定<strong>總持股上限（25% ~ 80%）與單筆權重乘數</strong>。
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-sky-900/60 text-sky-300 border border-sky-500/40">
                        LAYER 2
                      </span>
                      <h3 className="font-bold text-slate-100 text-sm">第二層：個股雙層共振掃描 (Action Scanner)</h3>
                    </div>
                    <p className="text-xs text-slate-300 leading-normal">
                      將大盤週期與全市場 1,826 檔個股所處之科斯托蘭尼階段進行 6×6 交叉矩陣運算。只有在大盤與個股訊號共振時才發出強力買進 (STRONG_BUY_A1)，並依據 4G 1% 風控原則計算精確整股與零股買進張數。
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-[#141923] border border-blue-500/30 flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-terminal-cyan mt-1.5 shrink-0"></div>
                  <div>
                    <span className="font-semibold text-slate-100 block mb-1">
                      核心操作三守則 (The Three Iron Rules)
                    </span>
                    <ul className="list-disc list-inside space-y-1 text-slate-300 text-xs">
                      <li><strong>絕不追高 A3 狂熱膨脹</strong>：小狗遠離主人 (+35% 乖離)，分批獲利了結。</li>
                      <li><strong>跌破均線 B1/B2 嚴格觀望</strong>：乘數強制歸零 (0.0x)，建議買進張數為 0。</li>
                      <li><strong>右側點火首選 A1 $\to$ A2</strong>：均線多頭翻揚、量縮吸籌後帶量突破是勝率最高的甜蜜波段。</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 2: CHARTS */}
          {activeSection === 'charts' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 2 · VISUALIZATION DEEP DIVE
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  核心圖表判讀手冊：代表什麼？怎麼看？
                </h2>
              </div>

              {/* Chart 1: Interactive Egg */}
              <div className="space-y-3 p-4 rounded-lg bg-[#0e1420] border border-[#1e293b]">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-100 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                    1. 科斯托蘭尼雞蛋順時針循環圖 (InteractiveEgg)
                  </h3>
                  <span className="text-[10px] font-mono text-sky-400 bg-sky-950/60 px-2 py-0.5 rounded border border-sky-500/30">
                    順時針循環 Y→A1→A2→A3→X→B1→B2→B3→Y
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                  以橢圓形軌跡呈現市場週期。左半弧為<strong>升市（Upward Arc，方向朝上 ↑）</strong>，右半弧為<strong>跌市（Downward Arc，方向朝下 ↓）</strong>。兩條水平虛線將雞蛋劃分為三個操作區域：
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-xs font-mono">
                  <div className="p-3 rounded bg-blue-950/50 border border-blue-500/30">
                    <span className="text-sky-300 font-bold block mb-1">買進區 (Buying: B3, A1)</span>
                    <span className="text-slate-300 text-[11px] font-sans">成交量萎縮築底或恐慌爆量斷頭，籌碼由猶豫者轉移至固執者手中。</span>
                  </div>
                  <div className="p-3 rounded bg-slate-900/60 border border-slate-700/40">
                    <span className="text-slate-300 font-bold block mb-1">隨波等待區 (Waiting: A2, B2)</span>
                    <span className="text-slate-400 text-[11px] font-sans">A2 升市中段順勢續抱；B2 跌市中段空頭主跌，保留現金嚴禁摸底。</span>
                  </div>
                  <div className="p-3 rounded bg-emerald-950/40 border border-emerald-500/30">
                    <span className="text-emerald-300 font-bold block mb-1">賣出區 (Selling: A3, B1)</span>
                    <span className="text-slate-300 text-[11px] font-sans">A3 狂熱過熱分批停利；B1 破線做頭反轉果斷停損出場。</span>
                  </div>
                </div>
              </div>

              {/* Chart 2: Breadth Stacked Area */}
              <div className="space-y-3 p-4 rounded-lg bg-[#0e1420] border border-[#1e293b]">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-100 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-terminal-cyan"></span>
                    2. 全市場 60D / 120D / 250D 循環深度堆疊圖 (BreadthChart)
                  </h3>
                  <span className="text-[10px] font-mono text-terminal-cyan bg-blue-950/60 px-2 py-0.5 rounded border border-blue-500/30">
                    全市場 1,826 檔內在結構溫度計
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                  統計全台灣上市櫃 1,826 檔股票在三大區域的每日分佈演變，並在上方疊加<strong>亮青色加權指數 (TAIEX) 曲線</strong>：
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs text-slate-300 font-sans">
                  <li><strong>底層藍色面積（買進區 B3+A1）</strong>：從低檔向上急遽放大突破 50%（如達 68%）時，為台股進入大牛市最強烈的底部確認訊號。</li>
                  <li><strong>上層綠色面積（賣出區 A3+B1）</strong>：若綠色面積擴大超過 25%，代表市場過熱或大量個股跌破短期均線做頭。</li>
                  <li><strong>指數與個股背離警訊（拉積盤）</strong>：若青色指數創新高，但底層藍色面積卻在萎縮、B1 破線家數增加，代表資金只集中於權值股，中小型股正在悄悄出貨。</li>
                </ul>
              </div>

              {/* Chart 3: Market Benchmarks */}
              <div className="space-y-3 p-4 rounded-lg bg-[#0e1420] border border-[#1e293b]">
                <h3 className="font-bold text-slate-100 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                  3. 六大市場基準卡 (MarketCards)
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                  上排包含<strong>加權指數 (TAIEX)</strong>、<strong>櫃買指數 (TPEx)</strong> 與<strong>MSCI台灣指數 (MSCI Taiwan)</strong>；下排包含<strong>元大台灣50 (0050.TW)</strong>、<strong>國泰永續高股息 (00878.TW)</strong> 與<strong>台指期近月 (TX Futures)</strong>。透過期貨正逆價差（Basis），可即時透視外資與主力大戶避險情緒。
                </p>
              </div>
            </div>
          )}

          {/* SECTION 3: WORKFLOW */}
          {activeSection === 'workflow' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 3 · STEP-BY-STEP INVESTMENT PLAYBOOK
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  如何尋找高勝率投資機會？實戰標準四步驟
                </h2>
              </div>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-900/60 text-terminal-cyan font-mono font-bold text-xs flex items-center justify-center shrink-0 border border-blue-500/40">
                    1
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-100">Step 1：確認大盤宏觀閘門狀態</h3>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans">
                      查看頂部狀態列的<strong>宏觀週期</strong>與<strong>做多/持有比例</strong>。當大盤處於 <code className="text-terminal-cyan">BULL_ZONE</code>（做多比例 &gt; 50%），權益上限為 80%、單筆權重乘數為 1.0x，代表環境具備系統性多頭順風，可積極挑選個股。
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-900/60 text-terminal-cyan font-mono font-bold text-xs flex items-center justify-center shrink-0 border border-blue-500/40">
                    2
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-100">Step 2：從「類股板塊熱力圖」鎖定主流強勢族群</h3>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans">
                      切換至<strong>「類股板塊熱力」</strong>，挑選 A1 與 A2 檔數最多、買進區佔比最高的優勢產業（例如半導體、電腦週邊、電子零組件）。點擊該產業的 A1 或 A2 儲存格，系統會自動跳轉至個股掃描並套用該產業過濾器。
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-900/60 text-terminal-cyan font-mono font-bold text-xs flex items-center justify-center shrink-0 border border-blue-500/40">
                    3
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-100">Step 3：在「個股掃描」精選雙層共振領頭羊</h3>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans">
                      在篩選表格中鎖定共振訊號為 <code className="text-terminal-cyan">STRONG_BUY_A1</code> 或 <code className="text-sky-300">TREND_HOLD_A2</code> 的標的，並檢查：
                    </p>
                    <ul className="list-disc list-inside text-xs text-slate-300 space-y-0.5 pt-1">
                      <li><strong>主人與狗乖離 (MA200 Bias)</strong>：位於 +2% ~ +18%（小狗剛起跑，未過熱）。</li>
                      <li><strong>成交量比 (VR60)</strong>：A1 階段 &lt; 0.85x（量縮洗盤）；A2 突破期 &gt; 1.20x（帶量點火）。</li>
                      <li><strong>相對強弱 (RS20)</strong>：&gt; 0（近 20 日強於加權指數）。</li>
                    </ul>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-900/60 text-terminal-cyan font-mono font-bold text-xs flex items-center justify-center shrink-0 border border-blue-500/40">
                    4
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-slate-100">Step 4：開啟「深度診斷抽屜」檢驗估值與計算部位</h3>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans">
                      點擊個股開啟右側抽屜，確認股價位於 <code className="text-terminal-cyan">2×2=5-1 估值階梯</code> 的合理中樞 (Base) 附近；接著在 4G 部位計算機輸入您的總資金，依據 2×ATR14 動態停損精確計算可買進的整股張數與零股股數。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 4: RISK & SIZING */}
          {activeSection === 'risk' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 4 · RISK MANAGEMENT & POSITION SIZING
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  風險控管與 4G 1% 資本部位計算機實戰
                </h2>
              </div>

              <div className="space-y-4 text-xs md:text-sm text-slate-300 leading-relaxed font-sans">
                <p>
                  科斯托蘭尼的 4G 哲學（Geld 資金、Gedanken 想法、Geduld 耐心、Glück 運氣）第一條即是<strong>資金管理</strong>。如果單筆交易虧損過大，任何投資策略都無法在長期存活。
                </p>

                {/* Sizing Mathematical Card */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-3 font-mono">
                  <div className="flex items-center justify-between">
                    <span className="text-terminal-cyan font-bold text-xs">4G 1% 風控公式核心推演</span>
                    <span className="text-[11px] text-slate-400">Risk-Budget Allocation</span>
                  </div>
                  <div className="p-3 rounded bg-[#0b0f17] border border-[#182232] text-xs text-slate-200 space-y-1">
                    <div>1. 單筆最大允許虧損預算：</div>
                    <div className="text-terminal-cyan font-bold pl-3">
                      Risk_Budget = Portfolio_Equity × 1.0% × Macro_Multiplier
                    </div>
                    <div>2. 每股停損價差（含來回手續費與證交稅）：</div>
                    <div className="text-sky-300 font-bold pl-3">
                      Loss_Per_Share = (Entry_Price - Stop_Loss) + Entry_Price × 0.00585
                    </div>
                    <div>3. 最大可買進總股數：</div>
                    <div className="text-emerald-300 font-bold pl-3">
                      Total_Shares = min( ⌊Risk_Budget / Loss_Per_Share⌋ , ⌊Portfolio_Equity × Max_Weight / Entry_Price⌋ )
                    </div>
                    <div className="pt-1 text-[11px] text-slate-400">
                      ⇒ 整股張數 = ⌊Total_Shares / 1,000⌋ 張；零股股數 = Total_Shares mod 1,000 股
                    </div>
                  </div>
                </div>

                {/* Microstructure Warnings */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-2">
                  <h3 className="font-bold text-slate-100 text-sm">台股微結構流動性風險標籤</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs pt-1">
                    <div className="p-2.5 rounded bg-red-950/40 border border-red-500/30">
                      <span className="text-red-300 font-bold block mb-1">處置股 (Disposition)</span>
                      <span className="text-slate-300 text-[11px]">人工撮合（5或20分鐘一盤），流動性受限無法即時停損，系統強制排除。</span>
                    </div>
                    <div className="p-2.5 rounded bg-amber-950/40 border border-amber-500/30">
                      <span className="text-amber-300 font-bold block mb-1">注意股 (Attention)</span>
                      <span className="text-slate-300 text-[11px]">短期週轉率或震幅劇烈，具隨時被關入處置之制度風險。</span>
                    </div>
                    <div className="p-2.5 rounded bg-purple-950/40 border border-purple-500/30">
                      <span className="text-purple-300 font-bold block mb-1">漲跌停鎖死 (Limit Locked)</span>
                      <span className="text-slate-300 text-[11px]">觸及 ±10% 漲跌停無量掛單，次日開盤具隔夜跳空滑價風險。</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 5: METRICS */}
          {activeSection === 'metrics' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 5 · QUANTITATIVE FORMULARY & BENCHMARKS
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  回測績效指標計算手冊與數學公式
                </h2>
              </div>

              <div className="space-y-4 text-xs md:text-sm text-slate-300 leading-relaxed font-sans">
                <p>
                  回測模組內建 8 大量化統計指標，用以客觀評定參數組在樣本外滾動時期的穩定度與風險回報比：
                </p>

                {/* Metric Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 font-mono">
                  {/* CAGR */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">複合年化報酬率 (CAGR)</span>
                      <span className="text-terminal-cyan text-[11px]">年化複利增長速度</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      CAGR = (E_N / E_0)^(250 / N) - 1
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      平滑多年總資產增長率，排除單次極端行情的視覺誤差。優質策略通常達 +20% 以上。
                    </p>
                  </div>

                  {/* Sharpe */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">夏普比率 (Sharpe Ratio)</span>
                      <span className="text-terminal-cyan text-[11px]">總波動風險回報比</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      Sharpe = [(R̄ - r_f/250) / σ_R] × √250
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      每承擔 1 單位總波動風險所換取的超額回報。&gt; 1.0 為良性，&gt; 2.0 為頂尖水準。
                    </p>
                  </div>

                  {/* Sortino */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">索提諾比率 (Sortino Ratio)</span>
                      <span className="text-terminal-cyan text-[11px]">純下行虧損懲罰</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      Sortino = [(R̄ - r_f/250) / σ_downside] × √250
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      只懲罰實質虧損的下行波動，完全不扣分向上的盈利暴賺跳升。更能反映投資人真實痛苦。
                    </p>
                  </div>

                  {/* MDD */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">最大資產回撤 (MDD)</span>
                      <span className="text-terminal-cyan text-[11px]">歷史最痛苦跌幅</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      MDD = max_t [ (Peak_t - E_t) / Peak_t ]
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      歷史上任一高點跌至谷底的最大跌幅。本系統透過宏觀空頭降倉，將 MDD 壓縮於 -15% 內。
                    </p>
                  </div>

                  {/* Calmar */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">卡爾瑪比率 (Calmar Ratio)</span>
                      <span className="text-terminal-cyan text-[11px]">收益回撤比</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      Calmar = CAGR / MDD
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      年化報酬與最大跌幅的比值。Calmar &gt; 1.5 代表年收益壓倒歷史最慘跌幅。
                    </p>
                  </div>

                  {/* Win Rate & Profit Factor */}
                  <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-100 font-bold text-xs">獲利因子 (Profit Factor)</span>
                      <span className="text-terminal-cyan text-[11px]">大賺小賠盈虧比</span>
                    </div>
                    <div className="p-2 rounded bg-[#0b0f17] text-[11px] text-sky-300 border border-[#182232]">
                      PF = ∑ Gains / |∑ Losses|
                    </div>
                    <p className="text-[11px] text-slate-400 font-sans">
                      所有盈利交易總和與虧損交易總和的比值。&gt; 2.0 代表賺到的錢是賠掉錢的兩倍以上。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 6: REAL-WORLD BACKTEST EXAMPLES */}
          {activeSection === 'examples' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                    CHAPTER 6 · EMPIRICAL VERIFICATION CASES
                  </span>
                  <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                    歷史真實回測範例 (Real-World Backtesting Case Studies)
                  </h2>
                </div>

                {/* Example Selector Tabs */}
                <div className="flex items-center gap-1.5 bg-[#141923] p-1 rounded-lg border border-[#1e293b]">
                  <button
                    type="button"
                    onClick={() => setSelectedExample('2330')}
                    className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors cursor-pointer ${
                      selectedExample === '2330'
                        ? 'bg-primary-container text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    台積電 (2330.TW)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedExample('2454')}
                    className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors cursor-pointer ${
                      selectedExample === '2454'
                        ? 'bg-primary-container text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    聯發科 (2454.TW)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedExample('TAIEX')}
                    className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors cursor-pointer ${
                      selectedExample === 'TAIEX'
                        ? 'bg-primary-container text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    加權指數 (TAIEX)
                  </button>
                </div>
              </div>

              {/* Case 1: TSMC (2330.TW) */}
              {selectedExample === '2330' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#182232] pb-2.5">
                      <div>
                        <span className="text-base font-bold text-slate-100">
                          案例 1：台積電 (2330.TW) 2021–2026 完整雞蛋循環回測
                        </span>
                        <div className="text-xs text-sky-400 font-mono mt-0.5">
                          半導體權值龍頭 · 逆勢恐慌吸籌至狂熱階梯停利
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded text-xs font-mono font-bold bg-emerald-950/70 text-emerald-300 border border-emerald-500/40">
                        策略累積報酬 +184.2% vs 買進持有 +68.5%
                      </span>
                    </div>

                    {/* Metric Scorecard */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 font-mono text-center">
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">波段勝率 (Win Rate)</span>
                        <span className="text-sm font-bold text-terminal-cyan">78.6%</span>
                        <span className="text-[10px] text-slate-400 block">11勝 / 3敗</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">最大回撤 (Max DD)</span>
                        <span className="text-sm font-bold text-emerald-400">-12.4%</span>
                        <span className="text-[10px] text-slate-400 block">買進持有為 -44.2%</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">夏普比率 (Sharpe)</span>
                        <span className="text-sm font-bold text-terminal-cyan">2.18</span>
                        <span className="text-[10px] text-slate-400 block">高波動抗震</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">獲利因子 (PF)</span>
                        <span className="text-sm font-bold text-sky-300">3.86</span>
                        <span className="text-[10px] text-slate-400 block">盈虧比優異</span>
                      </div>
                    </div>

                    {/* Timeline Walkthrough */}
                    <div className="space-y-2 text-xs text-slate-300 leading-relaxed font-sans pt-1">
                      <h4 className="font-bold text-slate-200">實戰週期推演與策略執行軌跡：</h4>
                      <ol className="list-decimal list-inside space-y-1.5 text-slate-300">
                        <li>
                          <strong className="text-sky-300">2022 年 10 月 (B3 恐慌出清 370 元)：</strong> 全球半導體庫存調整與地緣政治恐慌，散戶與融資斷頭斷在最低點。主人與狗乖離率跌至 -28%，VR60 萎縮惜售，系統觸發逆勢吸籌建倉訊號，部位計算機依 1% 風控首批建倉。
                        </li>
                        <li>
                          <strong className="text-sky-300">2023 年上半年 (A1 升市初段築底 500 元)：</strong> 股價重回季線 (60MA)，成交量溫和放大，固執投資人籌碼沉澱完畢，系統發出 STRONG_BUY_A1 第二筆加碼訊號。
                        </li>
                        <li>
                          <strong className="text-sky-300">2024 年上半年 (A2 升市中段點火 800 元)：</strong> AI 晶片需求爆發，帶量突破年線展開主升段，乘數維持 1.0x，沿 20MA 與 2×ATR14 動態停損順勢續抱，完全不輕易下車。
                        </li>
                        <li>
                          <strong className="text-sky-300">2024 年下半年 (A3 升市末段過熱 1,080 元)：</strong> 衝破千元大關爆出千億天量，正乖離率衝破 +45% 進入 A3 狂熱膨脹，系統自動將建議乘數降為 0.25x，啟動階梯停利，避開後續震盪回測。
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {/* Case 2: MediaTek (2454.TW) */}
              {selectedExample === '2454' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#182232] pb-2.5">
                      <div>
                        <span className="text-base font-bold text-slate-100">
                          案例 2：聯發科 (2454.TW) 板塊穿透與帶量突破回測
                        </span>
                        <div className="text-xs text-sky-400 font-mono mt-0.5">
                          IC 設計領頭羊 · 550 元築底至 1,500 元波段翻倍
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded text-xs font-mono font-bold bg-emerald-950/70 text-emerald-300 border border-emerald-500/40">
                        策略複合年化 CAGR +38.6%
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 font-mono text-center">
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">波段勝率</span>
                        <span className="text-sm font-bold text-terminal-cyan">71.4%</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">獲利因子</span>
                        <span className="text-sm font-bold text-sky-300">3.42</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">最大回撤</span>
                        <span className="text-sm font-bold text-emerald-400">-15.1%</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">索提諾比率</span>
                        <span className="text-sm font-bold text-terminal-cyan">2.64</span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed font-sans pt-1">
                      透過<strong>「類股板塊熱力圖」</strong>穿透，當半導體整體買進區比例跨越 60% 時，聯發科從 550 元 A1 築底區帶量跳空發動。在 700 元回踩 20MA 箱型底完成洗盤後，系統發出 TREND_HOLD_A2 順勢持有，以 2×ATR14 動態保利機制完整抱到 1,500 元，實踐「截斷虧損、讓利潤奔馳」。
                    </p>
                  </div>
                </div>
              )}

              {/* Case 3: TAIEX Macro Gate Timing vs Buy & Hold */}
              {selectedExample === 'TAIEX' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#182232] pb-2.5">
                      <div>
                        <span className="text-base font-bold text-slate-100">
                          案例 3：台股加權指數 (TAIEX) 宏觀閘門動態擇時 vs 買進持有 (Buy & Hold)
                        </span>
                        <div className="text-xs text-sky-400 font-mono mt-0.5">
                          全市場 1,826 檔廣度宏觀擇時驗證 (2021–2026)
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded text-xs font-mono font-bold bg-blue-950/70 text-terminal-cyan border border-blue-500/40">
                        夏普 1.84 vs 0.62 · MDD 壓縮 55%
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 font-mono text-center">
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">宏觀擇時 CAGR</span>
                        <span className="text-sm font-bold text-terminal-cyan">+26.4%</span>
                        <span className="text-[10px] text-slate-400 block">大盤買進持有為 +12.8%</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">策略最大回撤</span>
                        <span className="text-sm font-bold text-emerald-400">-14.2%</span>
                        <span className="text-[10px] text-slate-400 block">大盤回撤為 -32.1%</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">卡爾瑪比率 (Calmar)</span>
                        <span className="text-sm font-bold text-sky-300">1.86</span>
                        <span className="text-[10px] text-slate-400 block">大盤為 0.40</span>
                      </div>
                      <div className="p-2.5 rounded bg-[#0b0f17] border border-[#182232]">
                        <span className="text-[10px] text-slate-400 block">索提諾比率</span>
                        <span className="text-sm font-bold text-terminal-cyan">2.45</span>
                        <span className="text-[10px] text-slate-400 block">防禦空頭下行</span>
                      </div>
                    </div>

                    <div className="space-y-2 text-xs text-slate-300 leading-relaxed font-sans pt-1">
                      <h4 className="font-bold text-slate-200">宏觀擇時為何大幅擊敗單純買進持有？</h4>
                      <p>
                        <strong>1. 避開 2022 空頭屠殺：</strong> 2022 年初當台指期爆發恐慌逆價差、全市場賣出區比例突破 60% 時，宏觀閘門迅速切換為 <code className="text-terminal-cyan">BEAR_ZONE</code>，總權益上限強制壓制在 25%，成功保全獲利與現金。
                      </p>
                      <p>
                        <strong>2. 捕捉 2023–2026 牛市主升段：</strong> 2023 年底全市場廣度買進區比例突破 68.2%，宏觀閘門全面升級為 <code className="text-terminal-cyan">BULL_ZONE</code>，總持股上限拉至 80%、單筆權重乘數 1.0x，讓部位在 AI 狂飆的大行情中充分享受複利增長。
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SECTION 7: CLOUDFLARE D1 */}
          {activeSection === 'd1' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 7 · EDGE INFRASTRUCTURE & GLOBAL SYNC
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  什麼是「儲存並同步至 D1」？背後架構與全球連鎖效應
                </h2>
              </div>

              <div className="space-y-4 text-xs md:text-sm text-slate-300 leading-relaxed font-sans">
                <p>
                  當您在「ML 工作室」微調 23 項參數並滿意回測結果後，點擊藍色的
                  <strong className="text-terminal-cyan">「儲存並同步至 D1」</strong>，系統會觸發一套高度可靠的分散式邊緣資料庫同步流程：
                </p>

                {/* 4 Steps Architecture Pipeline */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 font-mono text-xs">
                  <div className="p-3 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                    <span className="text-[10px] text-sky-400 font-bold block">STEP 1</span>
                    <span className="text-slate-100 font-bold block">客戶端發送請求</span>
                    <p className="text-[11px] text-slate-400 font-sans">
                      攜帶安全 Bearer 金鑰發送 POST /api/v1/params，將新參數打包傳輸。
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                    <span className="text-[10px] text-sky-400 font-bold block">STEP 2</span>
                    <span className="text-slate-100 font-bold block">邊緣防護網檢驗</span>
                    <p className="text-[11px] text-slate-400 font-sans">
                      Worker 比對物理上下限 [min, max]，若數值越界立刻返回 422 阻斷，保護模型。
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                    <span className="text-[10px] text-sky-400 font-bold block">STEP 3</span>
                    <span className="text-slate-100 font-bold block">D1 原子事務寫入</span>
                    <p className="text-[11px] text-slate-400 font-sans">
                      在分散式 SQLite 執行 UPDATE model_parameters，更新時間戳記與生效值。
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                    <span className="text-[10px] text-sky-400 font-bold block">STEP 4</span>
                    <span className="text-slate-100 font-bold block">全網快取失效</span>
                    <p className="text-[11px] text-slate-400 font-sans">
                      清除全球 CDN 快取標頭，確保 300+ 城市在 60 秒內讀取最新模型。
                    </p>
                  </div>
                </div>

                {/* 4 Downstream Ripple Effects */}
                <div className="p-4 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-3">
                  <h3 className="font-bold text-slate-100 text-sm">四大深遠連鎖效應 (Downstream Ripple Effects)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="p-3 rounded bg-[#0b0f17] border border-[#182232] space-y-1">
                      <span className="text-terminal-cyan font-bold block">效應 1：每日全市場 1,826 檔重算基準</span>
                      <p className="text-slate-400 text-[11px] font-sans">
                        盤後排程腳本會直接讀取 D1 參數，全台股個股階段分類將依照新標準重新產生。
                      </p>
                    </div>
                    <div className="p-3 rounded bg-[#0b0f17] border border-[#182232] space-y-1">
                      <span className="text-terminal-cyan font-bold block">效應 2：大盤宏觀閘門與權益上限重估</span>
                      <p className="text-slate-400 text-[11px] font-sans">
                        買進區佔比跨越門檻時，大盤可能升級為 BULL_ZONE，將全組合做多上限由 50% 提高至 80%。
                      </p>
                    </div>
                    <div className="p-3 rounded bg-[#0b0f17] border border-[#182232] space-y-1">
                      <span className="text-terminal-cyan font-bold block">效應 3：4G 1% 風控計算機同步連鎖</span>
                      <p className="text-slate-400 text-[11px] font-sans">
                        宏觀乘數或 ATR 改變會直接傳導至「深度診斷抽屜」，全世界使用者計算建議買進張數時全面自動對齊。
                      </p>
                    </div>
                    <div className="p-3 rounded bg-[#0b0f17] border border-[#182232] space-y-1">
                      <span className="text-terminal-cyan font-bold block">效應 4：全球邊緣訪客一致性</span>
                      <p className="text-slate-400 text-[11px] font-sans">
                        所有連線訪客直接從就近的 Cloudflare 邊緣節點載入經過驗證的生產模型，無任何延遲。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 8: FAQ */}
          {activeSection === 'faq' && (
            <div className="stitch-card rounded-xl p-5 md:p-6 border border-[#182232] bg-[#0b0f17] space-y-5">
              <div className="border-b border-[#182232] pb-3">
                <span className="text-[11px] font-mono text-terminal-cyan uppercase font-semibold">
                  CHAPTER 8 · FREQUENTLY ASKED QUESTIONS
                </span>
                <h2 className="text-lg md:text-xl font-bold text-slate-100 mt-1">
                  常見問題解答 (FAQ) 與疑難排解
                </h2>
              </div>

              <div className="space-y-3">
                <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                  <h3 className="font-bold text-slate-100 text-xs md:text-sm">
                    Q1：在 ML 工作室拖曳參數滑桿，會影響其他造訪者嗎？
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    <strong>不會。</strong> 在 ML 工作室調整參數純粹是在您的個人瀏覽器記憶體中執行「What-If 即時模擬」（50 毫秒極速重算），完全不會修改伺服器資料庫。只有當您主動點擊「儲存並同步至 D1」時，才會寫入全球邊緣資料庫。
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                  <h3 className="font-bold text-slate-100 text-xs md:text-sm">
                    Q2：為什麼空頭市場（B2 階段），4G 計算機建議張數永遠是 0？
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    科斯托蘭尼強調在主跌段中摸底無異於飛蛾撲火。當個股或大盤落入 B2 空頭階段，系統的矩陣乘數會<strong>強制歸零 (0.0x)</strong>，計算機因而嚴格輸出 0 張，防止逆勢接刀損耗本金。
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                  <h3 className="font-bold text-slate-100 text-xs md:text-sm">
                    Q3：如何一鍵還原為最標準的教科書設定？
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    在網頁右上角下拉選單選擇 <code className="text-terminal-cyan">Default Kostolany v1</code>，或在 ML 工作室點擊灰色的<strong>「重置預設值」</strong>，系統會立即以標準的 20/60/200 日均線配置全面重算全市場。
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-[#0e1420] border border-[#1e293b] space-y-1">
                  <h3 className="font-bold text-slate-100 text-xs md:text-sm">
                    Q4：全市場 1,826 檔每日數據是如何更新的？
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    本系統設置了每日盤後自動化腳本（<code>scripts/generate_full_universe.py</code>）與 GitHub Actions CI/CD 排程。在每個台股交易日下午 15:30 收盤定價結算後，自動拉取當日 TAIEX、TPEx 與全市場標的進行指標演算並發布最新快照。
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
